import {
  FC,
  forwardRef,
  useRef,
  useState,
  useEffect,
  useCallback,
  useImperativeHandle,
  SyntheticEvent,
  type CSSProperties,
} from "react";
import Autocomplete, { createFilterOptions } from "@mui/material/Autocomplete";
import Chip from "@mui/material/Chip";
import TextFieldComponent from "@CubeComponents/Mui_Form/TextFieldComponent";
import { MultiSelectComponentProps } from "../../interface";

type Option = Record<string, unknown>;

const isStringArray = (arr: unknown[]): arr is string[] => arr.length === 0 || typeof arr[0] === "string";

const MultiSelectComponent: FC<MultiSelectComponentProps & Record<string, any>> = forwardRef((props, ref) => {
  const {
    dataSource = [],
    fields = { text: "text", value: "value" },
    value: valueProp,
    placeholder,
    enabled = true,
    change,
    onChange,
    select,
    removed,
    width,
    widthInPercentage,
    allowCustomValue,
    mode: _mode,
    created,
    valueTemplate,
    tagging: _tagging,
    showClearButton,
    disableChipBackground,
    ...rest
  } = props;

  const autocompleteRef = useRef<HTMLDivElement | null>(null);

  const rawOptions: unknown[] = Array.isArray(dataSource) ? dataSource : [];
  const stringMode = isStringArray(rawOptions);
  const textField = fields?.text || "text";
  const valueField = fields?.value || "value";

  // Normalize string arrays into objects so the rest of the logic is uniform
  const normalizeOptions = useCallback(
    (raw: unknown[]): Option[] =>
      isStringArray(raw) ? raw.map((s) => ({ [valueField]: s, [textField]: s })) : (raw as Option[]),
    [valueField, textField],
  );

  const options: Option[] = normalizeOptions(rawOptions);

  // Resolve a value prop (array of primitives or objects) into Option[]
  const resolveFromProp = useCallback(
    (val: unknown): Option[] => {
      if (!val) return [];
      const arr = Array.isArray(val) ? val : [val];
      return arr.map((v) => {
        if (typeof v === "object" && v !== null) return v as Option;
        return options.find((o) => o[valueField] === v) ?? ({ [valueField]: v, [textField]: v } as Option);
      });
    },
    [options, valueField, textField],
  );

  // Internal state — source of truth for the selected chips
  const [selected, setSelected] = useState<Option[]>(() => resolveFromProp(valueProp));

  // Sync internal state when the external value prop changes
  const prevValuePropRef = useRef(valueProp);
  useEffect(() => {
    if (prevValuePropRef.current !== valueProp) {
      prevValuePropRef.current = valueProp;
      setSelected(resolveFromProp(valueProp));
    }
  }, [valueProp, resolveFromProp]);

  // Expose imperative handle so consumers can do ref.current.value = [...] (Syncfusion compat)
  useImperativeHandle(ref, () => {
    const handle: any = autocompleteRef.current ?? {};
    Object.defineProperty(handle, "value", {
      get: () => selected.map((o) => o[valueField]),
      set: (newVal: unknown) => {
        setSelected(resolveFromProp(newVal));
      },
      configurable: true,
    });
    return handle;
  }, [selected, valueField, resolveFromProp]);

  // Fire `created` callback on mount (Syncfusion compat)
  const createdRef = useRef(created);
  createdRef.current = created;
  useEffect(() => {
    if (createdRef.current) createdRef.current();
  }, []);

  const getRawValue = (o: Option) => o[valueField];

  const handleChange = (_: SyntheticEvent, newValue: (Option | string)[]) => {
    // freeSolo can return raw strings for newly typed values — normalize them
    const normalized: Option[] = newValue.map((v) => {
      if (typeof v === "string") return { [valueField]: v, [textField]: v } as Option;
      return v as Option;
    });

    const prev = selected;
    const values = normalized.map((o) => getRawValue(o));

    // Detect added item
    if (normalized.length > prev.length) {
      const added = normalized.at(-1);
      const callbackItem = stringMode ? getRawValue(added!) : added;
      if (select) {
        const args: any = { itemData: callbackItem, name: "select", cancel: false };
        select(args);
        if (args.cancel) return;
      }
    }

    // Detect removed item
    if (normalized.length < prev.length) {
      const removedItem = prev.find((p) => !normalized.some((n) => n[valueField] === p[valueField]));
      if (removedItem && removed) {
        const callbackItem = stringMode ? getRawValue(removedItem) : removedItem;
        const args: any = { itemData: callbackItem, name: "removed", cancel: false };
        removed(args);
        if (args.cancel) return;
      }
    }

    // Update internal state
    setSelected(normalized);

    if (change) change({ value: values, itemData: stringMode ? values : normalized });
    if (onChange) onChange({ value: values, itemData: stringMode ? values : normalized });
  };

  const containerStyle: CSSProperties = {
    width: width || widthInPercentage || "100%",
  };

  const filter = createFilterOptions<Option>();

  return (
    <div style={containerStyle}>
      <Autocomplete
        ref={autocompleteRef}
        multiple
        freeSolo={!!allowCustomValue}
        options={options}
        value={selected}
        disabled={!enabled}
        disableClearable={showClearButton === false}
        getOptionLabel={(option: Option | string) => {
          if (typeof option === "string") return option;
          return String(option[textField] ?? "");
        }}
        isOptionEqualToValue={(option: Option, val: Option) => option[valueField] === val[valueField]}
        filterOptions={
          allowCustomValue
            ? (opts, params) => {
                const filtered = filter(opts, params);
                const { inputValue } = params;
                if (inputValue !== "" && !opts.some((o) => o[valueField] === inputValue)) {
                  filtered.push({ [valueField]: inputValue, [textField]: inputValue } as Option);
                }
                return filtered;
              }
            : undefined
        }
        onChange={handleChange}
        renderTags={(tagValue, getTagProps) =>
          tagValue.map((option, index) => {
            const { key, ...tagProps } = getTagProps({ index });

            // Support custom valueTemplate (Syncfusion compat)
            if (valueTemplate) {
              return (
                <span key={key} {...tagProps}>
                  {valueTemplate(typeof option === "string" ? { [valueField]: option, [textField]: option } : option)}
                </span>
              );
            }

            const label = typeof option === "string" ? option : String(option[textField] ?? "");
            return (
              <Chip
                key={key}
                label={label}
                {...tagProps}
                size="small"
                sx={disableChipBackground ? { backgroundColor: "transparent", border: "none" } : undefined}
              />
            );
          })
        }
        renderInput={(params) => (
          <TextFieldComponent
            {...params}
            placeholder={selected.length === 0 ? (placeholder as string | undefined) : undefined}
          />
        )}
        {...rest}
      />
    </div>
  );
});

MultiSelectComponent.displayName = "MultiSelectComponent";
export default MultiSelectComponent;
